import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { formatDistanceToNowStrict, parseISO } from 'date-fns'
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  ListChecks,
  Mail,
  MessageSquare,
  Phone,
  PhoneOff,
  Plus,
  Search,
  Settings,
  Sparkles,
  SquareKanban,
  Upload,
  UserPlus,
  Wallet,
  X,
  Zap,
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import {
  Board,
  BoardCard,
  BoardColumn,
  CHANNEL_OPTIONS,
  CardFieldKey,
  NO_REPLY_CLOSE_AFTER,
  NOTES_WARNING,
  addCardEvent,
  boardProfile,
  cardTitle,
  createCard,
  createCardLog,
  createCardNote,
  deleteCard,
  entryColumn,
  fetchBoards,
  fetchCards,
  fetchNoAnswerCounts,
  fetchOpenTaskCounts,
  fetchQueuedMessageCounts,
  fieldDef,
  isStuck,
  missingFieldsFor,
  nextPositionInColumn,
  optionLabel,
  topPositionInColumn,
  updateBoardThreshold,
  updateCard,
} from '../lib/boards'
import { BoardSettingsPanel } from '../components/boards/BoardSettingsPanel'
import { CardModal, StaffOption } from '../components/boards/CardModal'
import { ImportCardsModal } from '../components/boards/ImportCardsModal'
import { MoveCardModal } from '../components/boards/MoveCardModal'
import { TutorOption, inputClass, labelClass } from '../components/boards/CardFieldInput'
import {
  Avatar,
  BoardTabs,
  columnAccent,
  ghostButton,
  iconButton,
  overlayClass,
  panelClass,
  primaryButton,
  secondaryButton,
} from '../components/boards/ui'
import { hasAdminRights, isSuperAdmin } from '../lib/roles'
import { useDragEdgeScroll } from '../lib/dragEdgeScroll'

type PendingMove = {
  card: BoardCard
  target: BoardColumn
  position: number
  missing: CardFieldKey[]
}

const columnShellClass = 'border-gray-200/70 dark:border-white/[0.06] bg-gray-100/70 dark:bg-white/[0.025]'

const REFRESH_MS = 60000

const boardsLoadError = (e: any): string =>
  e?.code === '42P01'
    ? 'The board tables do not exist yet. Run migrations/v4-client-boards.sql on this database first.'
    : e?.message || 'Could not load the boards.'

export function Boards() {
  const { profile } = useAuth()
  const actorEmail = profile?.email ?? null
  const superAdmin = isSuperAdmin(profile?.role)
  const canImport = hasAdminRights(profile?.role)
  const [searchParams, setSearchParams] = useSearchParams()

  const [boards, setBoards] = useState<Board[]>([])
  const [allColumns, setAllColumns] = useState<BoardColumn[]>([])
  const [cards, setCards] = useState<BoardCard[]>([])
  const [noAnswerCounts, setNoAnswerCounts] = useState<Record<string, number>>({})
  const [openTaskCounts, setOpenTaskCounts] = useState<Record<string, number>>({})
  const [queuedMessageCounts, setQueuedMessageCounts] = useState<Record<string, number>>({})
  const [tutors, setTutors] = useState<TutorOption[]>([])
  const [staff, setStaff] = useState<StaffOption[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const searchRef = useRef<HTMLInputElement>(null)

  const [draggingId, setDraggingId] = useState<string | null>(null)
  const boardScrollRef = useRef<HTMLDivElement>(null)
  useDragEdgeScroll(boardScrollRef, draggingId !== null)
  const [dragOverColumnId, setDragOverColumnId] = useState<string | null>(null)
  const [dropBeforeId, setDropBeforeId] = useState<string | null>(null)

  const [openCardId, setOpenCardId] = useState<string | null>(searchParams.get('card'))
  const [pendingMove, setPendingMove] = useState<PendingMove | null>(null)
  const [showNew, setShowNew] = useState(false)
  const [showImport, setShowImport] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [notice, setNotice] = useState<{ text: string; boardKey: string; cardId: string } | null>(null)

  // Counts changes made on this page, so a background refresh that started before one is dropped.
  const localEdits = useRef(0)
  const editCards = (update: (prev: BoardCard[]) => BoardCard[]) => {
    localEdits.current++
    setCards(update)
  }

  const boardKey = searchParams.get('board') ?? 'families'
  const board = boards.find(b => b.key === boardKey) ?? boards[0]
  const boardId = board?.id
  const columns = useMemo(
    () => allColumns.filter(c => c.boardId === board?.id).sort((a, b) => a.sortOrder - b.sortOrder),
    [allColumns, board?.id]
  )

  // Boards and their columns. Used when the page opens and again when Settings opens.
  const loadBoards = useCallback(async () => {
    const data = await fetchBoards()
    setBoards(data.boards)
    setAllColumns(data.columns)
    return data.boards
  }, [])

  useEffect(() => {
    ;(async () => {
      try {
        const [loadedBoards, t, s] = await Promise.all([
          loadBoards(),
          supabase.from('tutors').select('id, name').order('name'),
          supabase.from('profiles').select('id, name, email').in('role', ['intake', 'admin', 'super_admin']).order('name'),
        ])
        setTutors((t.data ?? []).map((r: any) => ({ id: r.id, name: r.name })))
        setStaff((s.data ?? []).map((r: any) => ({ id: r.id, name: r.name || r.email })))
        if (loadedBoards.length === 0) setLoading(false)
      } catch (e: any) {
        setError(boardsLoadError(e))
        setLoading(false)
      }
    })()
  }, [loadBoards])

  const refreshCounts = useCallback(async (list: BoardCard[]) => {
    try {
      const ids = list.map(c => c.id)
      const [na, ot, qm] = await Promise.all([
        fetchNoAnswerCounts(ids),
        fetchOpenTaskCounts(ids),
        fetchQueuedMessageCounts(ids),
      ])
      setNoAnswerCounts(na)
      setOpenTaskCounts(ot)
      setQueuedMessageCounts(qm)
    } catch (e) {
      console.error('Could not load card counts (non-fatal):', e)
    }
  }, [])

  // Keyed on the board's id: reloading the boards (Settings) gives `board` a new identity, and that
  // shouldn't reload every card.
  const loadCards = useCallback(async () => {
    if (!boardId) return
    setLoading(true)
    localEdits.current++
    try {
      const list = await fetchCards(boardId)
      setCards(list)
      setError(null)
      await refreshCounts(list)
    } catch (e: any) {
      setError(e?.message || 'Could not load the cards.')
    } finally {
      setLoading(false)
    }
  }, [boardId, refreshCounts])

  useEffect(() => {
    loadCards()
  }, [loadCards])

  // Texts, calls and the website form add cards and "New log" badges without anyone touching the
  // page, so the board reloads every minute and when the tab is shown or focused again.
  useEffect(() => {
    if (!boardId) return
    let cancelled = false
    let lastRefresh = Date.now()
    const refresh = async () => {
      if (document.visibilityState !== 'visible' || Date.now() - lastRefresh < 5000) return
      lastRefresh = Date.now()
      const edits = localEdits.current
      try {
        const list = await fetchCards(boardId)
        if (cancelled || edits !== localEdits.current) return
        setCards(list)
        await refreshCounts(list)
      } catch (e) {
        console.error('Could not refresh the board (non-fatal):', e)
      }
    }
    const timer = setInterval(refresh, REFRESH_MS)
    window.addEventListener('focus', refresh)
    document.addEventListener('visibilitychange', refresh)
    return () => {
      cancelled = true
      clearInterval(timer)
      window.removeEventListener('focus', refresh)
      document.removeEventListener('visibilitychange', refresh)
    }
  }, [boardId, refreshCounts])

  useEffect(() => {
    if (!notice) return
    const t = setTimeout(() => setNotice(null), 6000)
    return () => clearTimeout(t)
  }, [notice])

  const dialogOpen = !!(openCardId || pendingMove || showNew || showImport || showSettings)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey || dialogOpen) return
      if ((e.target as HTMLElement).closest('input, textarea, select, [contenteditable="true"]')) return
      e.preventDefault()
      searchRef.current?.focus()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [dialogOpen])

  const tutorName = useMemo(() => Object.fromEntries(tutors.map(t => [t.id, t.name])), [tutors])

  const visibleCards = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return cards
    const digits = q.replace(/\D/g, '')
    return cards.filter(c => {
      const text = [c.parentName, c.studentFirstName, c.email, c.subjects, c.area, c.janeNumber && `jane #${c.janeNumber}`]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return text.includes(q) || (digits.length >= 3 && (c.phone ?? '').replace(/\D/g, '').includes(digits))
    })
  }, [cards, query])

  const cardsByColumn = useMemo(() => {
    const map: Record<string, BoardCard[]> = {}
    for (const col of columns) map[col.id] = []
    for (const c of visibleCards) (map[c.columnId] ??= []).push(c)
    for (const id of Object.keys(map)) map[id].sort((a, b) => a.position - b.position)
    return map
  }, [columns, visibleCards])

  const stats = useMemo(() => {
    const columnById = Object.fromEntries(columns.map(c => [c.id, c]))
    return {
      stuck: cards.filter(c => columnById[c.columnId] && isStuck(c, columnById[c.columnId])).length,
      unreviewed: cards.filter(c => c.isUnreviewed).length,
      newLogs: cards.filter(c => c.hasNewLog && !c.isUnreviewed).length,
    }
  }, [cards, columns])

  const positionFor = (columnId: string, beforeId: string | null, movingId: string): number => {
    const list = cards.filter(c => c.columnId === columnId && c.id !== movingId).sort((a, b) => a.position - b.position)
    if (list.length === 0) return 0
    const idx = beforeId ? list.findIndex(c => c.id === beforeId) : -1
    if (idx === -1) return list[list.length - 1].position + 1
    if (idx === 0) return list[0].position - 1
    return (list[idx - 1].position + list[idx].position) / 2
  }

  const closeCard = () => {
    setOpenCardId(null)
    if (searchParams.has('card')) setSearchParams({ board: board?.key ?? 'families' }, { replace: true })
  }

  const commitMove = async (card: BoardCard, target: BoardColumn, position: number, patch: Partial<BoardCard> = {}) => {
    const from = columns.find(c => c.id === card.columnId)
    const changingColumn = card.columnId !== target.id
    const changingBoard = card.boardId !== target.boardId
    const fullPatch: Partial<BoardCard> = { ...patch, columnId: target.id, position }
    if (changingBoard) fullPatch.boardId = target.boardId
    if (changingColumn && from?.kind === 'parked') {
      for (const f of from.entryFields) if (!(f in patch)) (fullPatch as any)[f] = null
      if (from.entryFields.includes('closeReason') && !('closeReasonOther' in patch)) fullPatch.closeReasonOther = null
    }

    const previous = cards
    editCards(prev =>
      changingBoard ? prev.filter(c => c.id !== card.id) : prev.map(c => (c.id === card.id ? { ...c, ...fullPatch } : c))
    )
    let updated: BoardCard
    try {
      updated = await updateCard(card.id, fullPatch)
    } catch (e: any) {
      editCards(() => previous)
      setError(e?.message || 'Could not move the card.')
      return
    }

    const targetBoard = boards.find(b => b.id === target.boardId)
    if (changingBoard) {
      closeCard()
      setNotice({
        text: `${cardTitle(updated)} moved to ${targetBoard?.name ?? 'another board'} · ${target.name}`,
        boardKey: targetBoard?.key ?? boardKey,
        cardId: card.id,
      })
    } else {
      editCards(prev => prev.map(c => (c.id === card.id ? updated : c)))
    }

    try {
      if (changingBoard) {
        await addCardEvent(card.id, 'updated', `Moved to the ${targetBoard?.name ?? 'other'} board`, actorEmail)
      }
      if (!changingColumn) return

      if (target.kind === 'parked') {
        const reason = updated.pauseReason
          || [optionLabel(fieldDef('closeReason', targetBoard?.key).options, updated.closeReason), updated.closeReasonOther].filter(Boolean).join(' — ')
        if (reason) await addCardEvent(card.id, 'updated', `Reason: ${reason}`, actorEmail)
      }
      if (target.key === 'no_answer') {
        await createCardLog(card.id, {
          channel: 'call', direction: 'out', outcome: 'No answer', noAnswer: true, followUpDate: null, note: null,
        })
        await refreshCounts(cards)
      }
    } catch (e: any) {
      setError(`${cardTitle(updated)} was moved, but the automatic note couldn't be saved: ${e?.message || 'unknown error'}`)
    }
  }

  const requestBoardMove = async (card: BoardCard, target: BoardColumn) => {
    let position: number
    try {
      position = await nextPositionInColumn(target.id)
    } catch (e: any) {
      setError(e?.message || 'Could not move the card.')
      return
    }
    const missing = missingFieldsFor(card, target, allColumns.filter(c => c.boardId === target.boardId))
    if (missing.length > 0) {
      setPendingMove({ card, target, position, missing })
      return
    }
    commitMove(card, target, position)
  }

  const requestMove = (card: BoardCard, target: BoardColumn, beforeId: string | null = null) => {
    if (card.boardId !== target.boardId) {
      requestBoardMove(card, target)
      return
    }
    const position = positionFor(target.id, beforeId, card.id)
    if (card.columnId === target.id) {
      commitMove(card, target, position)
      return
    }
    const missing = missingFieldsFor(card, target, columns)
    if (missing.length > 0) {
      setPendingMove({ card, target, position, missing })
      return
    }
    commitMove(card, target, position)
  }

  const closeNoReply = (card: BoardCard) => {
    const closed = columns.find(c => c.key === 'closed')
    if (!closed) return
    commitMove(card, closed, positionFor(closed.id, null, card.id), { closeReason: 'no_reply_3_tries' })
  }

  const handleDelete = async (card: BoardCard) => {
    try {
      await deleteCard(card.id)
      editCards(prev => prev.filter(c => c.id !== card.id))
      setOpenCardId(null)
    } catch (e: any) {
      setError(e?.message || 'Could not delete the card.')
    }
  }

  const changeThreshold = async (sessions: number) => {
    if (!board) return
    const before = board.paymentDueThresholdSessions
    setBoards(prev => prev.map(b => (b.id === board.id ? { ...b, paymentDueThresholdSessions: sessions } : b)))
    try {
      await updateBoardThreshold(board.id, sessions)
    } catch (e: any) {
      setBoards(prev => prev.map(b => (b.id === board.id ? { ...b, paymentDueThresholdSessions: before } : b)))
      setError(e?.message || 'Could not change the threshold.')
    }
  }

  const reloadBoards = async (): Promise<boolean> => {
    try {
      await loadBoards()
      return true
    } catch (e: any) {
      setError(boardsLoadError(e))
      return false
    }
  }

  // The columns were loaded when the page opened, maybe hours ago, and another super admin may
  // have changed them since. Settings works out column order from them, so it opens only once
  // they are loaded fresh; if that fails, the error shows on the board instead.
  const openSettings = async () => {
    if (await reloadBoards()) setShowSettings(true)
  }

  const handleDrop = (column: BoardColumn) => {
    const card = cards.find(c => c.id === draggingId)
    const beforeId = dropBeforeId
    setDraggingId(null)
    setDragOverColumnId(null)
    setDropBeforeId(null)
    if (card && beforeId !== card.id) requestMove(card, column, beforeId)
  }

  const openCard = cards.find(c => c.id === openCardId) ?? null
  // All of a column's cards, not just those matching the search: deleting a column deletes them all.
  const cardCountsByColumn = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const c of cards) counts[c.columnId] = (counts[c.columnId] ?? 0) + 1
    return counts
  }, [cards])
  const startColumn = entryColumn(columns)

  return (
    <div className="flex flex-col gap-5 h-full min-h-0">
      <div className="flex-shrink-0 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-10 w-10 flex-shrink-0 rounded-xl bg-brand-400/10 text-brand-600 dark:text-brand-400 flex items-center justify-center">
            <SquareKanban className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">Client Boards</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
              {board?.description ?? 'Track every family from first enquiry to paying student.'}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <input
              ref={searchRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setQuery('')
                  e.currentTarget.blur()
                }
              }}
              placeholder="Search name or phone"
              aria-label="Search cards"
              className="h-9 w-64 max-w-full pl-9 pr-9 rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 shadow-sm transition focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-400/25"
            />
            {query ? (
              <button
                onClick={() => setQuery('')}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            ) : (
              <kbd className="absolute right-2.5 top-1/2 -translate-y-1/2 hidden sm:inline-flex items-center h-5 px-1.5 rounded border border-gray-200 dark:border-white/10 font-sans text-[10px] font-medium text-gray-400 pointer-events-none">
                /
              </kbd>
            )}
          </div>
          {canImport && (
            <button onClick={() => setShowImport(true)} disabled={!board} className={`${secondaryButton} px-3`} title="Import cards from a CSV file">
              <Upload className="h-4 w-4" />
              <span className="hidden sm:inline">Import</span>
            </button>
          )}
          {superAdmin && (
            <button onClick={openSettings} disabled={!board} className={`${secondaryButton} px-3`} title="Board settings">
              <Settings className="h-4 w-4" />
              <span className="hidden sm:inline">Settings</span>
            </button>
          )}
          <button onClick={() => setShowNew(true)} disabled={!board || columns.length === 0} className={primaryButton}>
            <Plus className="h-4 w-4" /> {boardProfile(board?.key).newCardTitle}
          </button>
        </div>
      </div>

      {boards.length > 0 && (
        <div className="flex-shrink-0 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <BoardTabs boards={boards} activeId={board?.id} onSelect={(b) => setSearchParams({ board: b.key })} />
            {!loading && (
              <div className="flex flex-wrap items-center gap-1.5 text-xs font-medium">
                <span className="px-2 py-1 rounded-md bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-300 tabular-nums">
                  {cards.length} {cards.length === 1 ? 'card' : 'cards'}
                </span>
                {stats.stuck > 0 && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-red-500/10 text-red-600 dark:text-red-400 tabular-nums">
                    <AlertTriangle className="h-3 w-3" /> {stats.stuck} stuck
                  </span>
                )}
                {stats.unreviewed > 0 && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-amber-500/10 text-amber-600 dark:text-amber-400 tabular-nums">
                    <Sparkles className="h-3 w-3" /> {stats.unreviewed} new
                  </span>
                )}
                {stats.newLogs > 0 && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-sky-500/10 text-sky-600 dark:text-sky-400 tabular-nums">
                    <MessageSquare className="h-3 w-3" /> {stats.newLogs} new {stats.newLogs === 1 ? 'log' : 'logs'}
                  </span>
                )}
              </div>
            )}
          </div>
          {board && boardProfile(board.key).hasPaymentThreshold && (
            <div className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-sm text-gray-600 dark:text-gray-300 shadow-sm">
              <Wallet className="h-4 w-4 text-gray-400" />
              Payment due at
              {superAdmin ? (
                <select
                  value={board.paymentDueThresholdSessions}
                  onChange={(e) => changeThreshold(parseInt(e.target.value, 10))}
                  aria-label="Payment due threshold"
                  className="h-7 px-1.5 rounded-md border-0 bg-gray-100 dark:bg-white/10 text-sm font-semibold text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-400/40"
                >
                  {[0, 1, 2, 3, 4, 5, 6, 8, 10].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              ) : (
                <span className="font-semibold text-gray-900 dark:text-white">{board.paymentDueThresholdSessions}</span>
              )}
              sessions left or fewer
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="flex-shrink-0 flex items-start gap-3 p-3 pr-2 rounded-xl border border-red-500/20 bg-red-500/10 text-sm text-red-600 dark:text-red-300">
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError(null)} aria-label="Dismiss" className="p-0.5 rounded hover:bg-red-500/10">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex-1 min-h-0 flex gap-3 overflow-hidden" aria-busy="true" aria-label="Loading cards">
          {[3, 2, 4, 1, 2].map((n, i) => (
            <div key={i} className={`flex-shrink-0 w-72 rounded-2xl border p-3 space-y-3 animate-pulse ${columnShellClass}`}>
              <div className="h-4 w-32 rounded bg-gray-200 dark:bg-white/10" />
              {Array.from({ length: n }, (_, j) => (
                <div key={j} className="h-24 rounded-xl bg-white dark:bg-white/[0.06]" />
              ))}
            </div>
          ))}
        </div>
      ) : (
        <div ref={boardScrollRef} className="flex-1 min-h-[24rem] flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
          {columns.map(col => {
            const list = cardsByColumn[col.id] ?? []
            const stuckCount = list.filter(c => isStuck(c, col)).length
            const isOver = dragOverColumnId === col.id
            const accent = columnAccent(col)
            return (
              <section
                key={col.id}
                aria-label={col.name}
                onDragOver={(e) => {
                  e.preventDefault()
                  if (dragOverColumnId !== col.id) setDragOverColumnId(col.id)
                }}
                onDragLeave={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                    setDragOverColumnId(null)
                    setDropBeforeId(null)
                  }
                }}
                onDrop={(e) => {
                  e.preventDefault()
                  handleDrop(col)
                }}
                className={`flex-shrink-0 w-72 h-full min-h-0 rounded-2xl border flex flex-col transition-colors ${
                  isOver ? 'border-brand-400/60 bg-brand-400/[0.06] ring-4 ring-brand-400/10' : columnShellClass
                }`}
              >
                <header className="flex-shrink-0 flex items-center gap-2 px-3 pt-3 pb-2">
                  <span className={`h-2 w-2 flex-shrink-0 rounded-full ${accent.dot}`} />
                  <h2 className="flex-1 min-w-0 text-[13px] font-semibold text-gray-800 dark:text-gray-100 truncate" title={col.helpText ?? col.name}>
                    {col.name}
                  </h2>
                  {col.kind === 'auto' && (
                    <span
                      className="inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500"
                      title="Moves automatically with payments"
                    >
                      <Zap className="h-3 w-3" /> Auto
                    </span>
                  )}
                  {stuckCount > 0 && (
                    <span className="text-[11px] px-1.5 py-0.5 rounded-md bg-red-500/10 text-red-600 dark:text-red-400 font-medium tabular-nums" title="Stuck cards">
                      {stuckCount} stuck
                    </span>
                  )}
                  <span className="min-w-[1.5rem] text-center text-[11px] font-medium px-1.5 py-0.5 rounded-md bg-white dark:bg-white/10 text-gray-600 dark:text-gray-300 shadow-sm dark:shadow-none tabular-nums">
                    {list.length}
                  </span>
                </header>

                <div className="px-2 pb-2 space-y-2 flex-1 min-h-0 overflow-y-auto">
                  {list.map(card => (
                    <BoardCardTile
                      key={card.id}
                      card={card}
                      column={col}
                      tutorName={card.tutorId ? tutorName[card.tutorId] : undefined}
                      noAnswerCount={noAnswerCounts[card.id] ?? 0}
                      openTasks={openTaskCounts[card.id] ?? 0}
                      queuedMessages={queuedMessageCounts[card.id] ?? 0}
                      dragging={draggingId === card.id}
                      dropIndicator={isOver && dropBeforeId === card.id && draggingId !== card.id}
                      onOpen={() => setOpenCardId(card.id)}
                      onDragStart={(e) => {
                        e.dataTransfer.effectAllowed = 'move'
                        e.dataTransfer.setData('text/plain', card.id)
                        setDraggingId(card.id)
                      }}
                      onDragEnd={() => {
                        setDraggingId(null)
                        setDragOverColumnId(null)
                        setDropBeforeId(null)
                      }}
                      onDragOver={() => {
                        if (dropBeforeId !== card.id) setDropBeforeId(card.id)
                      }}
                    />
                  ))}
                  {list.length > 0 && isOver && dropBeforeId === null && draggingId && <DropLine />}
                  {list.length === 0 && (
                    <div
                      className={`flex items-center justify-center h-20 rounded-xl border border-dashed text-xs transition-colors ${
                        isOver ? 'border-brand-400/60 text-brand-600 dark:text-brand-400' : 'border-gray-300 dark:border-white/10 text-gray-400 dark:text-gray-500'
                      }`}
                    >
                      {draggingId ? 'Drop here' : query ? 'No matches' : 'No cards'}
                    </div>
                  )}
                </div>
              </section>
            )
          })}
        </div>
      )}

      {openCard && (
        <CardModal
          card={openCard}
          boards={boards}
          columns={allColumns}
          tutors={tutors}
          staff={staff}
          actorEmail={actorEmail}
          noAnswerCount={noAnswerCounts[openCard.id] ?? 0}
          onClose={closeCard}
          onCardUpdated={(updated) => editCards(prev => prev.map(c => (c.id === updated.id ? updated : c)))}
          onMoveRequest={(card, target) => requestMove(card, target)}
          onCloseNoReply={closeNoReply}
          onDelete={handleDelete}
          onCountsChanged={() => refreshCounts(cards)}
        />
      )}

      {pendingMove && (
        <MoveCardModal
          card={pendingMove.card}
          target={pendingMove.target}
          boards={boards}
          columns={allColumns}
          tutors={tutors}
          missing={pendingMove.missing}
          onCancel={() => setPendingMove(null)}
          onConfirm={async (patch) => {
            await commitMove(pendingMove.card, pendingMove.target, pendingMove.position, patch)
            setPendingMove(null)
          }}
        />
      )}

      {showNew && board && startColumn && (
        <NewCardModal
          board={board}
          firstColumn={startColumn}
          onCancel={() => setShowNew(false)}
          onCreate={async (values, note) => {
            const first = startColumn
            // From the database, not this page's copy: texts, calls and forms may have added
            // cards on top since the board last loaded.
            const created = await createCard(board.id, first.id, values, await topPositionInColumn(first.id))
            if (note.trim()) await createCardNote(created.id, note, actorEmail)
            editCards(prev => [...prev, created])
            setShowNew(false)
            setOpenCardId(created.id)
          }}
        />
      )}

      {showImport && board && (
        <ImportCardsModal
          boards={boards}
          columns={allColumns}
          tutors={tutors}
          initialBoardId={board.id}
          actorEmail={actorEmail}
          onClose={(importedInto) => {
            setShowImport(false)
            if (!importedInto) return
            if (importedInto.id === board.id) loadCards()
            else setSearchParams({ board: importedInto.key })
          }}
        />
      )}

      {showSettings && superAdmin && board && (
        <BoardSettingsPanel
          board={board}
          columns={columns}
          cardCounts={cardCountsByColumn}
          onColumnsChange={setAllColumns}
          onColumnDeleted={(columnId) => editCards(prev => prev.filter(c => c.columnId !== columnId))}
          onCardsStale={loadCards}
          onReload={reloadBoards}
          onClose={() => setShowSettings(false)}
        />
      )}

      {notice && (
        <div
          role="status"
          className="fixed bottom-6 right-6 z-40 max-w-sm flex items-center gap-3 p-3 pl-4 rounded-xl border border-green-500/20 bg-white/95 dark:bg-surface-100/95 shadow-lg backdrop-blur animate-modalIn text-sm text-gray-700 dark:text-gray-200"
        >
          <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-green-500" />
          <span className="flex-1">{notice.text}</span>
          <button
            onClick={() => {
              setSearchParams({ board: notice.boardKey, card: notice.cardId })
              setOpenCardId(notice.cardId)
              setNotice(null)
            }}
            className="font-medium text-brand-600 dark:text-brand-400 hover:underline"
          >
            Open
          </button>
          <button onClick={() => setNotice(null)} aria-label="Dismiss" className="p-0.5 rounded text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  )
}

function DropLine() {
  return <div className="h-0.5 rounded-full bg-brand-400 ring-4 ring-brand-400/15" />
}

const badgeClass = 'inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded-md tabular-nums'

function BoardCardTile({
  card,
  column,
  tutorName,
  noAnswerCount,
  openTasks,
  queuedMessages,
  dragging,
  dropIndicator,
  onOpen,
  onDragStart,
  onDragEnd,
  onDragOver,
}: {
  card: BoardCard
  column: BoardColumn
  tutorName?: string
  noAnswerCount: number
  openTasks: number
  queuedMessages: number
  dragging: boolean
  dropIndicator: boolean
  onOpen: () => void
  onDragStart: (e: React.DragEvent) => void
  onDragEnd: () => void
  onDragOver: () => void
}) {
  const stuck = isStuck(card, column)
  const subtitle = [card.grade && `Gr ${card.grade}`, card.subjects].filter(Boolean).join(' · ')

  return (
    <>
      {dropIndicator && <DropLine />}
      <article
        draggable
        role="button"
        tabIndex={0}
        aria-label={cardTitle(card)}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragOver={onDragOver}
        onClick={onOpen}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onOpen()
          }
        }}
        className={`group p-3 rounded-xl bg-white dark:bg-gray-800/50 border cursor-pointer select-none shadow-sm transition-all hover:shadow-md hover:-translate-y-px focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/50 ${
          stuck
            ? 'border-red-400/60 dark:border-red-500/40'
            : 'border-gray-200/80 dark:border-white/[0.08] hover:border-gray-300 dark:hover:border-white/15'
        } ${dragging ? 'opacity-40' : ''}`}
      >
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 leading-snug">{cardTitle(card)}</p>
          {card.isUnreviewed ? (
            <span
              className="flex-shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-md bg-amber-500/10 text-amber-600 dark:text-amber-400"
              title="New, unreviewed"
            >
              <Sparkles className="h-3 w-3" /> New
            </span>
          ) : card.hasNewLog && (
            <span
              className="flex-shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-md bg-sky-500/10 text-sky-600 dark:text-sky-400"
              title="A text, call or web form was logged since the card was last opened"
            >
              <MessageSquare className="h-3 w-3" /> New log
            </span>
          )}
        </div>
        {subtitle && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">{subtitle}</p>}

        {(card.phone || tutorName) && (
          <div className="mt-2 space-y-1">
            {card.phone && (
              <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                <Phone className="h-3 w-3" /> {card.phone}
              </p>
            )}
            {tutorName && (
              <p className="text-xs text-gray-600 dark:text-gray-300 flex items-center gap-1.5 min-w-0" title={`Tutor: ${tutorName}`}>
                <Avatar name={tutorName} /> <span className="truncate">{tutorName}</span>
              </p>
            )}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-1.5 mt-2.5 pt-2.5 border-t border-gray-100 dark:border-white/[0.06]">
          {stuck && (
            <span className={`${badgeClass} bg-red-500/10 text-red-600 dark:text-red-400`}>
              <AlertTriangle className="h-3 w-3" /> Stuck
            </span>
          )}
          {column.key === 'no_answer' && noAnswerCount > 0 && (
            <span
              className={`${badgeClass} ${
                noAnswerCount >= NO_REPLY_CLOSE_AFTER ? 'bg-red-500/10 text-red-600 dark:text-red-400' : 'bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-300'
              }`}
            >
              <PhoneOff className="h-3 w-3" /> {noAnswerCount}/{NO_REPLY_CLOSE_AFTER} tries
            </span>
          )}
          {queuedMessages > 0 && (
            <span className={`${badgeClass} bg-amber-500/10 text-amber-600 dark:text-amber-400`} title="Email to the family waiting to be sent">
              <Mail className="h-3 w-3" /> to send
            </span>
          )}
          {openTasks > 0 && (
            <span className={`${badgeClass} bg-brand-400/10 text-brand-600 dark:text-brand-400`} title="Open tasks">
              <ListChecks className="h-3 w-3" /> {openTasks}
            </span>
          )}
          <span className="ml-auto text-[11px] text-gray-400 dark:text-gray-500 flex items-center gap-1 tabular-nums" title="Time in this column">
            <Clock className="h-3 w-3" /> {formatDistanceToNowStrict(parseISO(card.columnEnteredAt))}
          </span>
        </div>
      </article>
    </>
  )
}

function NewCardModal({
  board,
  firstColumn,
  onCancel,
  onCreate,
}: {
  board: Board
  firstColumn: BoardColumn
  onCancel: () => void
  onCreate: (values: Partial<BoardCard>, note: string) => Promise<void>
}) {
  const [values, setValues] = useState<Partial<BoardCard>>({
    channel: 'call',
    paymentMethod: board.key === 'clinic' ? 'clinic' : null,
  })
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const set = (k: keyof BoardCard) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setValues(v => ({ ...v, [k]: e.target.value }))

  const submit = async () => {
    if (!values.parentName?.trim() && !values.phone?.trim()) {
      setError('Enter at least a name or a phone number.')
      return
    }
    setSaving(true)
    setError(null)
    try {
      await onCreate(values, note)
    } catch (e: any) {
      setError(e?.message || 'Could not create the card.')
      setSaving(false)
    }
  }

  return (
    <div className={`${overlayClass} z-50`}>
      <div className={`${panelClass} w-full max-w-md`} role="dialog" aria-modal="true" aria-label={boardProfile(board.key).newCardTitle}>
        <div className="flex items-start justify-between gap-4 p-6 pb-5">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 flex-shrink-0 rounded-xl bg-brand-400/10 text-brand-600 dark:text-brand-400 flex items-center justify-center">
              <UserPlus className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{boardProfile(board.key).newCardTitle}</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {board.name} board · starts in {firstColumn.name}
              </p>
            </div>
          </div>
          <button onClick={onCancel} aria-label="Close" className={iconButton}>
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 space-y-4">
          <div>
            <label className={labelClass}>{fieldDef('parentName', board.key).label}</label>
            <input value={values.parentName ?? ''} onChange={set('parentName')} className={inputClass} autoFocus />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Phone</label>
              <input type="tel" value={values.phone ?? ''} onChange={set('phone')} placeholder="403 555 0123" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Came in by</label>
              <select value={values.channel ?? ''} onChange={set('channel')} className={inputClass}>
                {CHANNEL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className={labelClass}>Email</label>
            <input type="email" value={values.email ?? ''} onChange={set('email')} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Notes</label>
            <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} className={inputClass} />
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-1.5 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3 flex-shrink-0" /> {NOTES_WARNING}
            </p>
          </div>
        </div>

        {error && <p className="px-6 mt-4 text-sm text-red-500">{error}</p>}

        <div className="flex justify-end gap-2 mt-6 px-6 py-4 border-t border-gray-100 dark:border-white/[0.06] bg-gray-50/80 dark:bg-white/[0.02] rounded-b-2xl">
          <button onClick={onCancel} className={ghostButton}>
            Cancel
          </button>
          <button onClick={submit} disabled={saving} className={primaryButton}>
            {saving ? 'Creating…' : 'Create card'}
          </button>
        </div>
      </div>
    </div>
  )
}
